import { apiClient } from 'shared/lib/apiClient';
import type {
  BusinessTrack,
  CourierBankAccountInfo,
  CourierDocument,
  CourierExperienceInfo,
  CourierPersonalInfo,
  CourierVehicleInfo,
  StepKey,
} from 'features/courier-onboarding/types';
import { normalizeEmail } from 'features/courier-onboarding/schemas';

export interface SubmitCourierPayload {
  businessTrack: BusinessTrack;
  personalInfo: CourierPersonalInfo;
  vehicleInfo: { vehicleTypes: CourierVehicleInfo['vehicleTypes'] };
  experienceInfo: CourierExperienceInfo;
  documents: CourierDocument[];
  bankAccountInfo?: CourierBankAccountInfo;
  notes?: string;
  metadata?: Record<string, unknown>;
  phoneVerificationToken: string;
}

export interface SubmitCourierResult {
  ok: boolean;
  submissionId: string;
  status: 'Submitted';
  persisted: boolean;
}

export const submitCourierOnboarding = async (
  payload: SubmitCourierPayload,
): Promise<SubmitCourierResult> => {
  const { data } = await apiClient.post<SubmitCourierResult>(
    '/api/courier-onboarding/submit',
    {
      ...payload,
      personalInfo: {
        ...payload.personalInfo,
        email: normalizeEmail(payload.personalInfo.email),
      },
    },
  );
  return data;
};

export interface CourierLeadResponse {
  leadId: string;
  fullName: string;
  phoneNumber: string;
  email: string;
  leadStatus: string;
  currentStep: StepKey;
  lastCompletedStep: StepKey | null;
  nextStep: StepKey;
  lastCompletedStepAt: string | null;
  firstReminderSentAt: string | null;
  finalReminderSentAt: string | null;
  needsPhoneCall: boolean;
  mondayLeadItemId: string | null;
  resumeUrl: string | null;
  source: string;
  draftData: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
}

export interface CreateCourierLeadResult extends CourierLeadResponse {
  resumeToken: string;
}

export const createCourierLead = async (payload: {
  fullName: string;
  phoneNumber: string;
  email: string;
  currentStep?: StepKey;
  nextStep?: StepKey;
  source?: string;
  metadata?: Record<string, unknown>;
}): Promise<CreateCourierLeadResult> => {
  const { data } = await apiClient.post<CreateCourierLeadResult>(
    '/api/courier-onboarding/leads',
    { ...payload, email: normalizeEmail(payload.email) },
  );
  return data;
};

export const updateCourierLeadProgress = async (
  leadId: string,
  payload: {
    lastCompletedStep: StepKey;
    currentStep: StepKey;
    nextStep: StepKey;
    draftData?: Record<string, unknown>;
    metadata?: Record<string, unknown>;
  },
): Promise<CourierLeadResponse> => {
  const { data } = await apiClient.patch<CourierLeadResponse>(
    `/api/courier-onboarding/leads/${leadId}/progress`,
    payload,
  );
  return data;
};

export interface CityEntry {
  /** Opaque Hashids-encoded id from the backend; treat as a string token. */
  id: string;
  name: string;
}

export const fetchCities = async (): Promise<CityEntry[]> => {
  const { data } = await apiClient.get<{ cities: CityEntry[] }>(
    '/api/courier-onboarding/cities',
  );
  return data.cities;
};

export const fetchSpokenLanguages = async (): Promise<string[]> => {
  const { data } = await apiClient.get<{ languages: string[] }>(
    '/api/courier-onboarding/spoken-languages',
  );
  return data.languages;
};

export interface VehicleOption {
  value: string;
  labelEn: string;
  labelHe: string;
  labelAr: string;
}

export const fetchVehicles = async (): Promise<VehicleOption[]> => {
  const { data } = await apiClient.get<{ vehicles: VehicleOption[] }>(
    '/api/courier-onboarding/vehicles',
  );
  return data.vehicles;
};

export const fetchExperienceRanges = async (): Promise<VehicleOption[]> => {
  const { data } = await apiClient.get<{ ranges: VehicleOption[] }>(
    '/api/courier-onboarding/experience-ranges',
  );
  return data.ranges;
};

export interface CheckTaxIdResult {
  exists: boolean;
}
export const checkTaxId = async (taxId: string): Promise<CheckTaxIdResult> => {
  const { data } = await apiClient.get<CheckTaxIdResult>(
    '/api/courier-onboarding/check-tax-id',
    { params: { taxId } },
  );
  return data;
};

export interface CheckRegistrationResult {
  exists: boolean;
}

/**
 * Checks whether a courier with the given phone and/or national ID has already
 * completed a registration. Used to block duplicate applications early in the
 * documents step and again at submit time.
 */
export const checkRegistration = async (params: {
  phone?: string;
  taxId?: string;
}): Promise<CheckRegistrationResult> => {
  const { data } = await apiClient.get<CheckRegistrationResult>(
    '/api/courier-onboarding/check-registration',
    { params },
  );
  return data;
};
